import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from '../ui/utils';
import { ChartNoAxesCombined } from 'lucide-react';

interface WellEntry {
    timestamp: string;
    oilProduction: number;
    waterProduction: number;
    staticPressure?: number;
    dropPressure?: number;
}

interface WellChartsProps {
    entries: WellEntry[];
    isDarkMode: boolean;
}

export function WellCharts({ entries, isDarkMode }: WellChartsProps) {
    const chartData = useMemo(() => {
        return entries.map(e => ({
            date: new Date(e.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            oil: Math.round(e.oilProduction),
            water: Math.round(e.waterProduction),
            gas: Math.round(e.staticPressure || 0),
        })).reverse(); // Assuming entries are new->old, charts usually want old->new for X axis left->right
    }, [entries]);

    if (entries.length === 0) {
        return (
            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                <CardContent className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                    <ChartNoAxesCombined className="w-8 h-8 opacity-20 mb-2" />
                    <p className="text-sm">No data available for charts</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                <CardHeader>
                    <CardTitle className={cn("text-base font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>
                        Production Trends (Oil vs Water)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e5e7eb'} vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#94a3b8' : '#64748b' }}
                                    axisLine={false}
                                    tickLine={false}
                                    dy={10}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#94a3b8' : '#64748b' }}
                                    axisLine={false}
                                    tickLine={false}
                                    dx={-10}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                                        borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                                        borderRadius: '12px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '12px',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                    cursor={{ stroke: isDarkMode ? '#475569' : '#cbd5e1', strokeWidth: 1 }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }} />
                                <Line
                                    type="monotone"
                                    dataKey="oil"
                                    name="Oil (BPD)"
                                    stroke="#10b981" // emerald-500
                                    strokeWidth={3}
                                    dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="water"
                                    name="Water (BPD)"
                                    stroke="#3b82f6" // blue-500
                                    strokeWidth={3}
                                    dot={{ r: 3, fill: '#3b82f6', strokeWidth: 0 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                <CardHeader>
                    <CardTitle className={cn("text-base font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>
                        Gas Production
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[200px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e5e7eb'} vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#94a3b8' : '#64748b' }}
                                    axisLine={false}
                                    tickLine={false}
                                    dy={10}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: isDarkMode ? '#94a3b8' : '#64748b' }}
                                    axisLine={false}
                                    tickLine={false}
                                    dx={-10}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                                        borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                                        borderRadius: '12px',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '12px',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                    cursor={{ stroke: isDarkMode ? '#475569' : '#cbd5e1', strokeWidth: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="gas"
                                    name="Gas (MCF)"
                                    stroke="#8b5cf6" // violet-500
                                    strokeWidth={3}
                                    dot={false}
                                    fill="url(#colorGas)"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
